import { useEffect, useMemo } from 'react';
import { decodeString, buildIframeUrl, appendIframeParam } from './utils';

const APP_URLS = {
  crm: 'https://app-next.findem-staging.com/crm',
  projects: 'https://app-next.findem-staging.com/projects',
  companyResearch: 'https://exec-staging.findem-staging.com/my-apps/company-builder?hideLayoutBars=true',
};

const useIframeMessage = (appName) => {
  const baseUrl = APP_URLS[appName];

  if (!baseUrl) {
    throw new Error(`Unknown app name: ${appName}. Valid options: ${Object.keys(APP_URLS).join(', ')}`);
  }

  const parsedBaseUrl = new URL(baseUrl);
  const { origin } = parsedBaseUrl;
  const baseSearchParams = parsedBaseUrl.search;

  // Construct iframe URL with deep link restoration from ?url= param
  const iframeUrl = useMemo(() => {
    const urlParam = new URLSearchParams(window.location.search).get('url');

    if (urlParam) {
      const constructedUrl = buildIframeUrl(origin, urlParam, baseSearchParams);
      if (constructedUrl) {
        return appendIframeParam(constructedUrl);
      }
    }

    return appendIframeParam(baseUrl);
  }, [origin, baseUrl, baseSearchParams]);

  // Listen for messages from iframe to update parent URL
  useEffect(() => {
    const handleMessage = (event) => {
      // Validate origin to prevent unauthorized URL changes from untrusted sources
      if (event.origin !== origin) {
        return;
      }

      if (event.data?.type === 'searchPageMsg' || event.data?.type === 'searchesPageMsg') {
        const { pathName } = event.data;

        if (pathName) {
          const url = new URL(`${window.location.origin}${window.location.pathname}`);
          url.searchParams.set('url', decodeString(pathName));
          window.history.pushState({}, '', url.toString());
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [origin]);

  return iframeUrl;
};

export default useIframeMessage;
